import {
	useDeleteAPI, useGetAPI, usePatchAPI, usePostAPI, usePutAPI
} from './useAPI';
import { useRecoilValue } from 'recoil';
import { CID, authenticatedUser, ActiveCarrier } from '@state';
import {
	AJAXError,
	APICallProps,
	TFinancialsDashboard,
	TMarketPlaceLoad,
	TMarketPlaceRules,
	PaginatedResponse,
	YourLoadItem,
	YourLoadsFilterCount,
	TEOSLoadMasterManifest,
	TETAReason,
	TMarketPlaceSchedule,
	TMeta,
	TBulkUploadDataResponse,
	TInvoiceBilling,
	AccountCode,
	AccountCodePayload,
	TCarrierDataExtract,
	TCarrierPrepDataExtract,
	TInvoiceFundingDetail,
	TInvoiceFundingInvoiceList,
	TCompanyDocument,
	TBulkAnalyticsResponse,
	TDataExtractTaskHistories,
	TAdminAvailableTeamResponse,
	TFleetDescription,
	TFleetListResponse,
	TFleetFormOptions,
	TCreateFleetPayload,
	ICarrierLane,
	ICarrierLanesOverview,
	ICarrierLanesListResponse,
	TCarrierFinance,
} from '@types';

export const useDownloadCarrierInvoice = () => {
	const cID = useRecoilValue(CID);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/invoices/{1}/download',
		headers: { 'X-Carrier-Token': `${cID}` },
	});

	return {
		loadingDownloadInvoice: isLoading,
		carrierInvoice: data as any,
		carrierInvoiceError: error,
		callDownloadCarrierInvoice: callAPI as (props?: APICallProps) => Promise<{
		data: any;
		error: AJAXError | null
	}>
	}
}

export const useOffersNotify = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	/* The api.php guard resolves the carrier from this header for admin users;
	   the web route used to get it from the session. */
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/carrier/fintech-offers',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		isLoading,
		offersNotifyResponse: data,
		offersNotifyError: error as AJAXError,
		callOffersNotify: callAPI as (props?: APICallProps) => Promise<{
data: any | null;
error: AJAXError | null;
}>
	};
};

export const useGetYourLoadCount = () => {
	const cID = useRecoilValue(CID);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/your-loads/count',
		headers: { 'X-Carrier-Token': `${cID}` },
	});

	return {
		loadingYourLoadCount: isLoading,
		yourLoad: data as any,
		yourLoadCountError: error,
		callGetYourLoadCount: callAPI as (props?: APICallProps) => Promise<{
		data: any;
		error: AJAXError | null
	}>
	}
}

export const useGetGroupLoadCount = () => {
	const cID = useRecoilValue(CID);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/group-loads/count',
		headers: { 'X-Carrier-Token': `${cID}` },
	});

	return {
		loadingGroupLoadCount: isLoading,
		groupLoad: data as any,
		groupLoadCountError: error,
		callGetGroupLoadCount: callAPI as (props?: APICallProps) => Promise<{
		data: any;
		error: AJAXError | null
	}>
	}
}

export const useGetYourLoadsData = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/your-loads',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingYourLoadData: isLoading,
		yourLoadsData: data as PaginatedResponse<YourLoadItem>,
		yourLoadsError: error,
		callGetYourLoadData: callAPI as (props?: APICallProps) => Promise<{
			data: PaginatedResponse<YourLoadItem>,
			error: AJAXError | null
		}>
	}
}

export const useGetLoadsToUpdateETA = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const query = '?filter[and][0][columnField]=status&filter[and][0][operatorValue]=manifestStatusIs&filter[and][0][value]=Not+Started,On+the+Road';
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/carrier/your-loads${query}`,
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingLoadsToUpdateETAData: isLoading,
		loadsToUpdateETAData: data as PaginatedResponse<YourLoadItem>,
		loadsToUpdateETAError: error,
		callGetLoadsToUpdateETAData: callAPI as (props?: APICallProps) => Promise<{
			data: PaginatedResponse<YourLoadItem>,
			error: AJAXError | null
		}>
	}
}

export const useGetYourLoadsFilterCount = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);

	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/your-loads/custom-filter-count',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingFilterCount: isLoading,
		filterCountsData: data?.data as YourLoadsFilterCount,
		error,
		callGetYourLoadFilterCount: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: YourLoadsFilterCount
			}
			error: AJAXError | null
		}>
	}
}

export const useUploadPOD = () => {
	const cID = useRecoilValue(CID);

	const {
		isLoading,
		data,
		error,
		callAPI,
	} = usePostAPI({
		endpoint: '',
		headers: { 'X-Carrier-Token': `${cID}`, },
	});

	const uploadPOD = async (
		manifestId: string,
		formData: FormData,
		options?: Partial<APICallProps>
	): Promise<{ data: any; error: AJAXError | null }> => {
		const res = await callAPI({
			payload: formData,
			overrideUrl: `/api/carrier/your-loads/manifest/${manifestId}/upload-pod`,
			headers: {
				'X-Carrier-Token': `${cID}`,
				'Content-Type': 'multipart/form-data',
				...options?.headers,
			},
			...options,
		});

		if (!res || res.error) throw res?.error ?? new Error('Unknown error');

		return res.data;
	};

	return {
		uploadingPOD: isLoading,
		uploadedData: data,
		uploadError: error,
		uploadPOD,
	};
};

export const useGetCarrierFinancialDashboard = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = (cID && cID > 0) ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/carrier/financials-dashboard`,
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingFinancialDashboard: isLoading,
		financialDashboardData: data as TFinancialsDashboard,
		financialDashboardError: error,
		callGetFinancialDashboard: callAPI as (props?: APICallProps) => Promise<{
			data: TFinancialsDashboard;
			error: AJAXError | null
		}>
	}
}

export const useGetCarrierFinancials = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = (cID && cID > 0) ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/carrier/financials`,
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingFinancial: isLoading,
		financialData: data as TInvoiceBilling,
		financialError: error,
		callGetFinancial: callAPI as (props?: APICallProps) => Promise<{
			data: TInvoiceBilling;
			error: AJAXError | null
		}>
	}
}

export const usePostMarketplaceBid = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: `/api/carrier/marketplace/job-list/{1}/bid`,
		headers: carrierToken ? { 'X-Carrier-Token': `${carrierToken}` } : {},
	});

	return {
		loadingMktBid: isLoading,
		mktBidData: data?.data as {
			master_manifest_id: number;
		},
		mktBidError: error,
		callPostMktBid: callAPI as (props?: APICallProps) => Promise<{
			data: {
				master_manifest_id: number;
			};
			error: AJAXError | null
		}>
	}
}

export const usePutWithdrawMarketplaceBid = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePutAPI({
		endpoint: `/api/carrier/marketplace/{1}/cancel`,
		headers: carrierToken ? { 'X-Carrier-Token': `${carrierToken}` } : {},
	});

	return {
		loadingWithdrawBid: isLoading,
		withdrawBidData: data?.data as {
			success: boolean
		},
		withdrawBidError: error,
		callPutWithdrawBid: callAPI as (props?: APICallProps) => Promise<{
			data: {
				success: boolean
			};
			error: AJAXError | null
		}>
	}
}

export const useGetETAReasons = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/eta-reasons',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingETAReasonsData: isLoading,
		ETAReasonsData: data?.data as TETAReason[],
		ETAReasonsError: error,
		callGetETAReasonsData: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TETAReason[]
			},
			error: AJAXError | null
		}>
	}
}

export const usePostRequestETAArrival = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/estimation-time-of-arrival-request',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingRequestETAArrivalData: isLoading,
		requestETAArrivalData: data?.data as TETAReason[],
		requestETAArrivalError: error,
		callGetRequestETAArrivalData: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: YourLoadItem
			},
			error: AJAXError | null
		}>
	}
}

export const usePodUnavailable = () => {
	const cID = useRecoilValue(CID);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: `/api/carrier/your-loads/manifests/{1}/update-pod-status`,
		headers: { 'X-Carrier-Token': `${cID}`, },
	});

	return {
		isLoading,
		data,
		error,
		callPodUnavailable: callAPI as (props?: APICallProps) => Promise<{
		error: AJAXError | null;
}>
	};
};


export const useGetPodData = () => {
	const cID = useRecoilValue(CID);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/your-loads/manifest/{1}/upload-pod',
		headers: { 'X-Carrier-Token': `${cID}` },
	});

	return {
		loadingPodData: isLoading,
		podData: data?.data,
		error,
		callGetPodData: callAPI as (props?: APICallProps) => Promise<{
		error: AJAXError | null
	}>
	}
}

export const useGetMarketplaceLoads = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/carrier/marketplace/job-list`,
		headers: carrierToken ? { 'X-Carrier-Token': `${carrierToken}` } : {},
	});

	return {
		loadingMktPlaceLoads: isLoading,
		mktPlaceLoadsData: data?.data as TMarketPlaceLoad,
		mktPlaceLoadsError: error,
		callGetMktPlaceLoads: callAPI as (props?: APICallProps) => Promise<{
			data: {
				isOpen: boolean;
				rules: TMarketPlaceRules;
				schedule: TMarketPlaceSchedule,
				job_list: TMarketPlaceLoad[];
				data: {
					job_list: TMarketPlaceLoad[];
					meta: TMeta;
				}
			};
			error: AJAXError | null
		}>
	}
}

export const useBulkPatchCargoUpdate = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/carrier/your-loads/cargos/bulk-update',
		headers: carrierToken ? { 'X-Carrier-Token': `${carrierToken}` } : {},
	});


	return {
		loadingBulkCargoUpdate: isLoading,
		bulkCargoUpdateData: data as {
			success: boolean;
			updatedIds: number[];
		},
		bulkCargoUpdateError: error,
		callPatchBulkCargoUpdate: callAPI as (props?: APICallProps) => Promise<{
			data: {
				success: boolean;
				updatedIds: number[];
			};
			error: AJAXError | null
		}>
	}
}

export const useGetLoadMasterManifestData = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/your-loads/master-manifest/{1}',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingLoadMasterManifestData: isLoading,
		loadMasterManifestData: data?.data as TEOSLoadMasterManifest,
		loadMasterManifestError: error,
		callGetLoadMasterManifestData: callAPI as (props?: APICallProps) => Promise<{
			data: {
				data: TEOSLoadMasterManifest
			},
			error: AJAXError | null
		}>
	}
}

/* 	Carrier -> Analytics */

export const useGetCarrierDataExtracts = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/carrier/analytics/data-extract-list/{1}', headers });

	return {
		loadingDataExtracts: isLoading,
		dataExtracts: data as TCarrierDataExtract[],
		getDataExtractsError: error as AJAXError,
		clearDataExtractsError: clearError,
		clearDataExtractsData: clearData,
		callGetDataExtracts: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierDataExtract[];
			error: AJAXError | null
		}>
	}
}


export const useRemoveDataExtractCarrier = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/carrier/analytics/data-extract-delete', headers });

	return {
		loadingRemoveDataExtract: isLoading,
		removeDataExtract: data as any,
		removeDataExtractError: error as AJAXError,
		clearRemoveDataExtractError: clearError,
		clearRemoveDataExtractData: clearData,
		callRemoveDataExtract: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	}
}

export const usePrepDownloadDataExtractCarrier = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/carrier/analytics/data-extract', headers });

	return {
		loadingPrepDataExtracts: isLoading,
		prepDataExtracts: data as TCarrierPrepDataExtract,
		getPrepDataExtractsError: error as AJAXError,
		clearPrepDataExtractsError: clearError,
		clearPrepDataExtractsData: clearData,
		callPrepDataExtracts: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierPrepDataExtract;
			error: AJAXError | null
		}>
	}
}


export const useDownloadCarrierReport = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/carrier/analytics/download-report/{1}/{2}', headers });

	return {
		loadingCarrierReport: isLoading,
		carrierReport: data as string,
		getCarrierrReportError: error as AJAXError,
		clearCarrierReportError: clearError,
		clearCarrierReportData: clearData,
		callDownloadCarrierReport: callAPI as (props?: APICallProps) => Promise<{
			data: string;
			error: AJAXError | null
		}>
	}
}

export const useGetBulkUploadData = () => {
	const cID = useRecoilValue(CID);
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/bulk-upload',
		headers: { 'X-Carrier-Token': `${cID}` },
	});

	return {
		loadingBulkUpload: isLoading,
		bulkUploadData: data as TBulkUploadDataResponse,
		error,
		callGetBulkUpload: callAPI as (props?: APICallProps) => Promise<{
		data: TBulkUploadDataResponse;
		error: AJAXError | null
	}>
	}
}

export const useGetCarrierInvoiceDataExtracts = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };

	const {
		isLoading,
		data,
		error,
		clearData,
		clearError,
		callAPI
	} = useGetAPI({ endpoint: '/api/carrier/analytics/data-extract-invoice-list/{1}', headers });

	return {
		loadingInvoiceDataExtracts: isLoading,
		invoicedataExtracts: data as TCarrierDataExtract[],
		getInvoiceDataExtractsError: error as AJAXError,
		clearInvoiceDataExtractsError: clearError,
		clearInvoiceDataExtractsData: clearData,
		callGetInvoiceDataExtracts: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierDataExtract[];
			error: AJAXError | null
		}>
	}
}

export const useGetBulkUploadReport = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };

	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI,
	} = useGetAPI({
		endpoint: '/api/carrier/bulk-upload/download-{1}/{2}',
		headers: headers,
	});

	return {
		loading: isLoading,
		bulkUploadReport: data as Blob,
		getBulkUploadReportError: error as AJAXError,
		clearBulkUploadReportError: clearError,
		callGetBulkUploadReport: callAPI as (props?: APICallProps) => Promise<{
	data: Blob;
	error: AJAXError | null;
	}>,
	};
};

export const usePostBulkUploadMilestone = () => {
	const cID = useRecoilValue(CID);

	const {
		isLoading,
		data,
		callAPI,
		error
	} = usePostAPI({
		endpoint: '/api/carrier/bulk-upload',
		headers: { 'X-Carrier-Token': `${cID}` },
	});

	const triggerAPI = async (payload: FormData) => {
		return await callAPI({ payload: payload });
	}

	return {
		loadingPostBulkUploadMilestone: isLoading,
		error,
		postBulkUploadMilestoneData: data,
		callPostBulkUploadMilestone: triggerAPI
	}
}

export const useCarrierSettings = () => {
	const cID = useRecoilValue(CID);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: `/api/carrier/admin-carrier-settings?cid=${cID}`,
		headers: { 'X-Carrier-Token': `${cID}` }
	});

	return {
		isLoading,
		settings: data,
		error: error as AJAXError,
		fetchSettings: callAPI as (props?: APICallProps) => Promise<{
			data: any | null;
			error: AJAXError | null;
		}>
	};
};

export const useUpdatePodAutoApprove = () => {
	const cID = useRecoilValue(CID);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: `/api/admin/carrier/${cID}/pod-auto-approve`,
		headers: { 'X-Carrier-Token': `${cID}` }
	});

	const triggerAPI = async (isEnabled: boolean) => {
		return await callAPI({ payload: { isPodAutoApprove: isEnabled } });
	};

	return {
		isLoading,
		data,
		error: error as AJAXError,
		updatePodAutoApprove: triggerAPI
	};
};

export const useUpdateMarketplaceStatus = () => {
	const cID = useRecoilValue(CID);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: `/api/admin/carrier/${cID}/toggle-marketplace-enabled`,
		headers: { 'X-Carrier-Token': `${cID}` }
	});

	const triggerAPI = async (isEnabled: boolean) => {
		return await callAPI({ payload: { isMarketplaceEnabled: isEnabled } });
	};

	return {
		isLoading,
		data,
		error: error as AJAXError,
		updateMarketplaceStatus: triggerAPI
	};
};

export const useDeactivateCarrier = () => {
	const cID = useRecoilValue(CID);

	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: `/api/admin/carrier/${cID}/carrier-deactivate?reason={1}`,
		headers: { 'X-Carrier-Token': `${cID}` }
	});

	const triggerAPI = async (reason: string): Promise<{ data: any | null; error: AJAXError | null }> => {
		return await (
		callAPI as (props?: APICallProps) => Promise<{ data: any | null; error: AJAXError | null }>
		)({ routes: [`${reason}`], payload: { cid: cID, isInbox: false } });
	};

	return {
		isLoading,
		data,
		error: error as AJAXError,
		deactivateCarrier: triggerAPI
	};
};


export const useGetAccountCodes = () => {
	const carrier = useRecoilValue(ActiveCarrier);
	const { isLoading, data, error, callAPI } = useGetAPI({ endpoint: `/api/admin/carrier/${carrier?.customer_id}/account-codes`, });

	const triggerAPI = async (props?: APICallProps) => {
		return await callAPI(props);
	};

	return {
		codes: (data?.data as AccountCode[]) || [],
		isLoading,
		error: error as AJAXError,
		getAccountCodes: triggerAPI as (props?: APICallProps) => Promise<{ data: AccountCode[]; error: AJAXError | null }>,
	};
};

export const useCreateAccountCode = () => {
	const carrier = useRecoilValue(ActiveCarrier);
	const { isLoading, data, error, callAPI } = usePostAPI({ endpoint: `/api/admin/carrier/${carrier?.customer_id}/account-codes`, });

	const createAccountCode = async (payload: AccountCodePayload) => {
		return await callAPI({ payload });
	};

	return {
		createAccountCode: createAccountCode as (payload: AccountCodePayload) => Promise<{
			data: AccountCode;
			error: AJAXError | null;
		}>,
		isLoading,
		data: data as AccountCode,
		error: error as AJAXError
	};
};

export const useUpdateAccountCode = () => {
	const carrier = useRecoilValue(ActiveCarrier);
	const { isLoading, data, error, callAPI } = usePutAPI({ endpoint: `/api/admin/carrier/${carrier?.customer_id}/account-codes/{1}`, });

	const updateAccountCode = async (id: string, payload: AccountCodePayload) => {
		return await callAPI({
			routes: [id],
			payload
		});
	};

	return {
		updateAccountCode: updateAccountCode as (id: string, payload: AccountCodePayload) => Promise<{
			data: AccountCode;
			error: AJAXError | null;
		}>,
		isLoading,
		data: data as AccountCode,
		error: error as AJAXError
	};
};

export const useDeleteAccountCode = () => {
	const carrier = useRecoilValue(ActiveCarrier);
	const { isLoading, data, error, callAPI } = useDeleteAPI({ endpoint: `/api/admin/carrier/${carrier?.customer_id}/account-codes/{1}`, });

	const deleteAccountCode = async (id: string) => {
		return await callAPI({ routes: [id] });
	};

	return {
		deleteAccountCode: deleteAccountCode as (id: string) => Promise<{
			data: { success: boolean };
			error: AJAXError | null;
		}>,
		isLoading,
		data: data as { success: boolean },
		error: error as AJAXError
	};
};

export const useGetInvoiceFundingDetail = () => {
	const cID = useRecoilValue(CID);
	const { isLoading, data, error, callAPI } = useGetAPI({
		endpoint: '/api/carrier/fintech/butn-registration-status',
		headers: { 'X-Carrier-Token': `${cID}` },
	});
	return {
		isLoadingFundingDetail: isLoading,
		fundingDetail: data as TInvoiceFundingDetail | null,
		fundingDetailError: error as AJAXError,
		callGetFundingDetail: callAPI as () => Promise<{ data: TInvoiceFundingDetail; error: AJAXError | null }>,
	};
};

export const useGetInvoiceFundingInvoices = () => {
	const cID = useRecoilValue(CID);
	const { isLoading, data, error, callAPI } = useGetAPI({
		endpoint: '/api/carrier/fintech/butn/transactions',
		headers: { 'X-Carrier-Token': `${cID}` },
	});
	return {
		isLoadingInvoices: isLoading,
		fundingInvoices: data as TInvoiceFundingInvoiceList | null,
		fundingInvoicesError: error as AJAXError,
		callGetFundingInvoices: callAPI as (props?: APICallProps) => Promise<{ data: TInvoiceFundingInvoiceList; error: AJAXError | null }>,
	};
};

export const useUploadFundingInvoice = () => {
	const cID = useRecoilValue(CID);
	const { isLoading, data, error, callAPI } = usePostAPI({
		endpoint: '/api/carrier/fintech/butn/{1}/transactions',
		headers: { 'X-Carrier-Token': `${cID}` },
	});
	return {
		isUploadingInvoice: isLoading,
		uploadInvoiceResponse: data,
		uploadInvoiceError: error as AJAXError,
		callUploadFundingInvoice: callAPI as (props?: APICallProps) => Promise<{ data: any; error: AJAXError | null }>,
	};
};

export const useIncreaseFundingLimit = () => {
	const cID = useRecoilValue(CID);
	const { isLoading, data, error, callAPI } = usePostAPI({
		endpoint: '/api/carrier/fintech/increase-funding-limit',
		headers: { 'X-Carrier-Token': `${cID}` },
	});
	return {
		isSubmittingLimitRequest: isLoading,
		increaseLimitResponse: data,
		increaseLimitError: error as AJAXError,
		callIncreaseLimit: callAPI as (props?: APICallProps) => Promise<{ data: any; error: AJAXError | null }>,
	};
};

export const useGetButnRegistrationLink = () => {
	const cID = useRecoilValue(CID);
	const { isLoading, error, callAPI } = usePostAPI({
		endpoint: '/api/carrier/fintech/butn-registration',
		headers: { 'X-Carrier-Token': `${cID}` },
	});
	return {
		isLoadingRegistrationLink: isLoading,
		registrationLinkError: error as AJAXError,
		callGetRegistrationLink: callAPI as (props?: APICallProps) => Promise<{ data: { redirect_uri: string } | null; error: AJAXError | null }>,
	};
};

export const useGetCarrierBatches = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };
	const { isLoading, data, error, clearData, clearError, callAPI } = useGetAPI({
		endpoint: '/api/carrier/analytics/data-extract/batches',
		headers,
	});
	return {
		loadingCarrierBatches: isLoading,
		carrierBatches: data as { rows: TBulkAnalyticsResponse[]; total: number },
		getCarrierBatchesError: error as AJAXError,
		clearCarrierBatchesError: clearError,
		clearCarrierBatchesData: clearData,
		callGetCarrierBatches: callAPI as (props?: APICallProps) => Promise<{ data: { rows: TBulkAnalyticsResponse[]; total: number }; error: AJAXError | null }>,
	};
};

export const useCreateCarrierBatch = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };
	const { isLoading, error, clearData, clearError, callAPI } = usePostAPI({
		endpoint: '/api/carrier/analytics/data-extract/batches',
		headers,
	});
	return {
		loadingCreateCarrierBatch: isLoading,
		createCarrierBatchError: error as AJAXError,
		clearCreateCarrierBatchError: clearError,
		clearCreateCarrierBatchData: clearData,
		callCreateCarrierBatch: callAPI as (props?: APICallProps) => Promise<{ data: TBulkAnalyticsResponse; error: AJAXError | null }>,
	};
};

export const useDeleteCarrierBatch = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };
	const { isLoading, error, clearData, clearError, callAPI } = useDeleteAPI({
		endpoint: '/api/carrier/analytics/data-extract/batches/{1}',
		headers,
	});
	return {
		loadingDeleteCarrierBatch: isLoading,
		deleteCarrierBatchError: error as AJAXError,
		clearDeleteCarrierBatchError: clearError,
		clearDeleteCarrierBatchData: clearData,
		callDeleteCarrierBatch: callAPI as (props?: APICallProps) => Promise<{ data: null; error: AJAXError | null }>,
	};
};

export const useGetDownloadCarrierBatchReport = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };
	const { data, isLoading, callAPI } = useGetAPI({
		endpoint: '/api/carrier/analytics/data-extract/batches/{1}/download',
		headers,
	});
	return {
		carrierBatchReportData: data as Blob | null,
		carrierBatchReportDownloading: isLoading,
		callDownloadCarrierBatchReport: callAPI as (props?: APICallProps) => Promise<{ data: Blob | null; error: AJAXError | null }>,
	};
};

export const useGetCarrierBatch = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };
	const { isLoading, data, error, clearData, clearError, callAPI } = useGetAPI({
		endpoint: '/api/carrier/analytics/data-extract/batches/{1}',
		headers,
	});
	return {
		loadingCarrierBatch: isLoading,
		carrierBatch: data as TBulkAnalyticsResponse,
		getCarrierBatchError: error as AJAXError,
		clearCarrierBatchError: clearError,
		clearCarrierBatchData: clearData,
		callGetCarrierBatch: callAPI as (props?: APICallProps) => Promise<{ data: TBulkAnalyticsResponse; error: AJAXError | null }>,
	};
};

export const useUpdateCarrierBatch = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const headers = { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` };
	const { isLoading, error, clearData, clearError, callAPI } = usePatchAPI({
		endpoint: '/api/carrier/analytics/data-extract/batches/{1}',
		headers,
	});
	return {
		loadingUpdateCarrierBatch: isLoading,
		updateCarrierBatchError: error as AJAXError,
		clearUpdateCarrierBatchError: clearError,
		clearUpdateCarrierBatchData: clearData,
		callUpdateCarrierBatch: callAPI as (props?: APICallProps) => Promise<{ data: TBulkAnalyticsResponse; error: AJAXError | null }>,
	};
};

export const useGetCarrierTeamsForDataExtract = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const { isLoading, data, error, callAPI } = useGetAPI({
		endpoint: '/api/carrier/analytics/data-extract/teams',
		headers: { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` },
	});
	return {
		loadingCarrierTeam: isLoading,
		getCarrierTeamError: error as AJAXError,
		carrierTeamDetails: data as { data: TAdminAvailableTeamResponse } | null,
		callGetCarrierTeamsForDataExtract: callAPI as (props?: APICallProps) => Promise<{ data: { data: TAdminAvailableTeamResponse } | null; error: AJAXError | null }>,
	};
};

export const useGetCarrierDataExtractTasksHistory = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const { isLoading, data, error, clearData, clearError, callAPI } = useGetAPI({
		endpoint: '/api/carrier/analytics/data-extract/tasks/histories',
		headers: { 'X-Carrier-Token': `${cID ?? authUser?.carrier?.id}` },
	});
	return {
		loadingCarrierTasksHistory: isLoading,
		carrierTasksHistory: data as TDataExtractTaskHistories,
		getCarrierTasksHistoryError: error as AJAXError,
		clearCarrierTasksHistoryError: clearError,
		clearCarrierTasksHistoryData: clearData,
		callGetCarrierTasksHistory: callAPI as (props?: APICallProps) => Promise<{ data: TDataExtractTaskHistories; error: AJAXError | null }>,
	};
};

/* 	Carrier -> Company Details -> Finance */

export const useGetCarrierFinance = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/finance',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingCarrierFinance: isLoading,
		carrierFinance: data as TCarrierFinance | null,
		carrierFinanceError: error as AJAXError,
		callGetCarrierFinance: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierFinance | null;
			error: AJAXError | null;
		}>
	};
};

export const useUpdateCarrierFinance = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/carrier/finance',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		isUpdatingCarrierFinance: isLoading,
		updateCarrierFinanceResponse: data as TCarrierFinance | null,
		updateCarrierFinanceError: error as AJAXError,
		callUpdateCarrierFinance: callAPI as (props?: APICallProps) => Promise<{
			data: TCarrierFinance | null;
			error: AJAXError | null;
		}>
	};
};

/* Carrier -> Document Hub */

export const useGetCarrierDocuments = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/documents',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		isLoadingDocuments: isLoading,
		documents: (Array.isArray(data) ? data : data?.data ?? []) as TCompanyDocument[],
		documentsError: error as AJAXError,
		callGetDocuments: callAPI as (props?: APICallProps) => Promise<{
			data: TCompanyDocument[] | { data: TCompanyDocument[] };
			error: AJAXError | null;
		}>,
	};
};

export const useUploadCarrierDocument = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/carrier/documents',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	const uploadDocument = async (formData: FormData) => {
		return await callAPI({
			payload: formData,
			headers: { 'Content-Type': 'multipart/form-data' },
			returnFullError: true,
		});
	};

	return {
		isUploadingDocument: isLoading,
		uploadDocumentResponse: data,
		uploadDocumentError: error as AJAXError,
		uploadDocument,
	};
};

export const useDeleteCarrierDocument = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/carrier/documents/{1}',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	const deleteDocument = async (id: number) => {
		return await callAPI({ routes: [`${id}`], returnFullError: true });
	};

	return {
		isDeletingDocument: isLoading,
		deleteDocumentResponse: data,
		deleteDocumentError: error as AJAXError,
		deleteDocument,
	};
};

export const useGetCarrierDocumentFile = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/documents/{1}',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	const getDocumentFile = async (id: number) => {
		return await callAPI({ routes: [`${id}`], responseType: 'blob' });
	};

	return {
		isLoadingDocumentFile: isLoading,
		documentFileError: error as AJAXError,
		getDocumentFile: getDocumentFile as (id: number) => Promise<{ data: Blob; error: AJAXError | null }>,
	};
};
/* Carrier -> Company Details -> Fleet */

export const useGetFleetDescription = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/fleets-overview',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	const triggerAPI = async (props?: APICallProps) => {
		return await callAPI({
			params: { cid: `${carrierToken ?? ''}` },
			...props,
		});
	};

	return {
		loadingFleetDescription: isLoading,
		fleetDescription: data as TFleetDescription | null,
		fleetDescriptionError: error as AJAXError,
		clearFleetDescriptionError: clearError,
		clearFleetDescriptionData: clearData,
		callGetFleetDescription: triggerAPI as (props?: APICallProps) => Promise<{
			data: TFleetDescription;
			error: AJAXError | null;
		}>
	};
};

export const useSaveFleetDescription = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({
		endpoint: '/api/carrier/fleets-overview',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	const triggerAPI = async (payload: TFleetDescription) => {
		return await callAPI({
			params: { cid: `${carrierToken ?? ''}` },
			payload,
		});
	};

	return {
		savingFleetDescription: isLoading,
		saveFleetDescriptionResponse: data,
		saveFleetDescriptionError: error as AJAXError,
		clearSaveFleetDescriptionError: clearError,
		callSaveFleetDescription: triggerAPI as (payload: TFleetDescription) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	};
};

export const useGetFleetList = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/fleet',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	const triggerAPI = async (page = 1, perPage = 25) => {
		return await callAPI({ params: { page: `${page}`, perPage: `${perPage}` } });
	};

	return {
		loadingFleetList: isLoading,
		fleetListData: data as TFleetListResponse | null,
		fleetListError: error as AJAXError,
		clearFleetListError: clearError,
		clearFleetListData: clearData,
		callGetFleetList: triggerAPI as (page?: number, perPage?: number) => Promise<{
			data: TFleetListResponse;
			error: AJAXError | null;
		}>
	};
};

export const useDeleteFleet = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/carrier/fleet/{1}',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	const triggerAPI = async (fleetId: number | string) => {
		return await callAPI({ routes: [`${fleetId}`] });
	};

	return {
		deletingFleet: isLoading,
		deleteFleetResponse: data,
		deleteFleetError: error as AJAXError,
		clearDeleteFleetError: clearError,
		callDeleteFleet: triggerAPI as (fleetId: number | string) => Promise<{
			data: any;
			error: AJAXError | null;
		}>
	};
};

export const useGetFleetFormOptions = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/fleet/form-options',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	return {
		loadingFleetFormOptions: isLoading,
		fleetFormOptions: data as TFleetFormOptions | null,
		fleetFormOptionsError: error as AJAXError,
		clearFleetFormOptionsError: clearError,
		clearFleetFormOptionsData: clearData,
		callGetFleetFormOptions: callAPI as (props?: APICallProps) => Promise<{
			data: TFleetFormOptions;
			error: AJAXError | null;
		}>
	};
};

export const useCreateFleet = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({
		endpoint: '/api/carrier/fleet',
		headers: { 'X-Carrier-Token': `${carrierToken}` }
	});

	const triggerAPI = async (payload: TCreateFleetPayload) => {
		return await callAPI({ payload });
	};

	return {
		creatingFleet: isLoading,
		createFleetResponse: data,
		createFleetError: error as AJAXError,
		clearCreateFleetError: clearError,
		callCreateFleet: triggerAPI as (payload: TCreateFleetPayload) => Promise<{
			data: { data: any };
			error: AJAXError | null;
		}>
	};
};
/* 	Carrier -> Company Details -> Lane */

export const useGetCarrierLanesOverview = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/carrier/lanes-overview', headers: { 'X-Carrier-Token': `${carrierToken}` } });

	const triggerAPI = async (props?: APICallProps) => {
		return await callAPI({ params: { cid: `${carrierToken ?? ''}` }, ...props });
	};

	return {
		loadingLanesOverview: isLoading,
		lanesOverview: data as ICarrierLanesOverview | null,
		lanesOverviewError: error as AJAXError,
		clearLanesOverviewError: clearError,
		clearLanesOverviewData: clearData,
		callGetLanesOverview: triggerAPI as (props?: APICallProps) => Promise<{
			data: ICarrierLanesOverview | null;
			error: AJAXError | null
		}>
	};
};

export const useSaveCarrierLanesOverview = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/carrier/lanes-overview', headers: { 'X-Carrier-Token': `${carrierToken}` } });

	const triggerAPI = async (payload: ICarrierLanesOverview) => {
		return await callAPI({ params: { cid: `${carrierToken ?? ''}` }, payload });
	};

	return {
		savingLanesOverview: isLoading,
		savedLanesOverview: data as ICarrierLanesOverview | null,
		saveLanesOverviewError: error as AJAXError,
		clearSaveLanesOverviewError: clearError,
		callSaveLanesOverview: triggerAPI as (payload: ICarrierLanesOverview) => Promise<{
			data: ICarrierLanesOverview | null;
			error: AJAXError | null
		}>
	};
};

export const useGetCarrierLanes = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		clearData,
		callAPI
	} = useGetAPI({ endpoint: '/api/carrier/lanes', headers: { 'X-Carrier-Token': `${carrierToken}` } });

	const triggerAPI = async (search?: string) => {
		const params: Record<string, string> = {};
		if (carrierToken) params.cid = `${carrierToken}`;
		if (search) params.search = search;
		return await callAPI({ params });
	};

	return {
		loadingCarrierLanes: isLoading,
		carrierLanes: data as ICarrierLanesListResponse | null,
		carrierLanesError: error as AJAXError,
		clearCarrierLanesError: clearError,
		clearCarrierLanesData: clearData,
		callGetCarrierLanes: triggerAPI as (search?: string) => Promise<{
			data: ICarrierLanesListResponse;
			error: AJAXError | null
		}>
	};
};

export const useAddCarrierLane = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = usePostAPI({ endpoint: '/api/carrier/lanes', headers: { 'X-Carrier-Token': `${carrierToken}` } });

	const triggerAPI = async (lane: ICarrierLane) => {
		return await callAPI({ params: { cid: `${carrierToken ?? ''}` }, payload: lane });
	};

	return {
		addingCarrierLane: isLoading,
		addedCarrierLane: data as ICarrierLane | null,
		addCarrierLaneError: error as AJAXError,
		clearAddCarrierLaneError: clearError,
		callAddCarrierLane: triggerAPI as (lane: ICarrierLane) => Promise<{
			data: ICarrierLane | null;
			error: AJAXError | null
		}>
	};
};

export const useDeleteCarrierLane = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		clearError,
		callAPI
	} = useDeleteAPI({ endpoint: '/api/carrier/lanes/{1}', headers: { 'X-Carrier-Token': `${carrierToken}` } });

	const triggerAPI = async (laneId: number | string) => {
		return await callAPI({ routes: [`${laneId}`], params: { cid: `${carrierToken ?? ''}` } });
	};

	return {
		deletingCarrierLane: isLoading,
		deleteCarrierLaneResponse: data as null,
		deleteCarrierLaneError: error as AJAXError,
		clearDeleteCarrierLaneError: clearError,
		callDeleteCarrierLane: triggerAPI as (laneId: number | string) => Promise<{
			data: null;
			error: AJAXError | null
		}>
	};
};
/*	Carrier -> Company Details -> Team */

export const useGetCarrierTeam = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useGetAPI({
		endpoint: '/api/carrier/team',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingCarrierTeam: isLoading,
		carrierTeam: data as any,
		carrierTeamError: error as AJAXError,
		callGetCarrierTeam: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	};
};

export const useDeleteCarrierTeamMember = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = useDeleteAPI({
		endpoint: '/api/carrier/team/{1}',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingDeleteTeamMember: isLoading,
		deleteTeamMemberData: data as any,
		deleteTeamMemberError: error as AJAXError,
		callDeleteTeamMember: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	};
};

export const useSaveCarrierTeamMember = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({
		endpoint: '/api/carrier/team',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingSaveTeamMember: isLoading,
		saveTeamMemberData: data as any,
		saveTeamMemberError: error as AJAXError,
		callSaveTeamMember: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	};
};

export const useUpdateCarrierTeamMember = () => {
	const cID = useRecoilValue(CID);
	const authUser = useRecoilValue(authenticatedUser);
	const carrierToken = cID && cID > 0 ? cID : authUser?.carrier?.id;
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePatchAPI({
		endpoint: '/api/carrier/team/{1}',
		headers: { 'X-Carrier-Token': `${carrierToken}` },
	});

	return {
		loadingUpdateTeamMember: isLoading,
		updateTeamMemberData: data as any,
		updateTeamMemberError: error as AJAXError,
		callUpdateTeamMember: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	};
};

export const useEmailCheck = () => {
	const {
		isLoading,
		data,
		error,
		callAPI
	} = usePostAPI({ endpoint: '/api/email-check', });

	return {
		loadingEmailCheck: isLoading,
		emailCheckData: data as any,
		emailCheckError: error as AJAXError,
		callEmailCheck: callAPI as (props?: APICallProps) => Promise<{
			data: any;
			error: AJAXError | null
		}>
	};
};
